import { useState, useCallback } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import SEOSchema from '@/components/base/SEOSchema';
import { aboutContent } from '@/mocks/about';

export default function About() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = useCallback(async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    setFormError('');

    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypotValue = (formData.get('website_alt') as string || '').trim();

    if (honeypotValue) {
      setFormStatus('success');
      setSubmitting(false);
      return;
    }

    const submitUrl = 'https://readdy.ai/api/form/d9tfn1lca23r0tr1ai10';
    const urlEncoded = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key !== 'website_alt') {
        urlEncoded.append(key, value as string);
      }
    });

    try {
      const response = await fetch(submitUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: urlEncoded.toString(),
      });
      const responseText = await response.text();
      let parsed: Record<string, unknown> = {};
      try { parsed = JSON.parse(responseText); } catch { /* ignore */ }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = (parsed?.meta as Record<string, string>)?.message
          || (parsed?.message as string)
          || responseText;
        if (typeof serverMsg === 'string' && serverMsg.includes('spam')) {
          setFormError('提交失败，请稍后再试');
        } else {
          setFormError(typeof serverMsg === 'string' ? serverMsg : '提交失败，请稍后再试');
        }
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请稍后再试');
      setFormStatus('error');
    } finally {
      setSubmitting(false);
    }
  }, []);

  const { brandIntro, milestones, reputation, contact } = aboutContent;

  return (
    <>
      <ScrollToTop />
      <SEOSchema
        type="Organization"
        data={{
          name: '香蕉视频',
          url: 'https://whtefafuwu.cn',
          description: brandIntro.description,
          foundingDate: '2020',
          address: {
            '@type': 'PostalAddress',
            streetAddress: brandIntro.headquarters,
            addressLocality: '北京',
            addressCountry: 'CN',
          },
          contactPoint: {
            '@type': 'ContactPoint',
            telephone: contact.phone,
            contactType: 'customer service',
            availableLanguage: ['Chinese'],
          },
        }}
      />
      <SEOSchema
        type="WebPage"
        data={{
          name: '关于香蕉视频 - 创意短视频平台',
          description: brandIntro.description,
          url: 'https://whtefafuwu.cn/about',
        }}
      />
      <SEOSchema
        type="BreadcrumbList"
        data={{
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: '首页', item: 'https://whtefafuwu.cn' },
            { '@type': 'ListItem', position: 2, name: '关于我们' },
          ],
        }}
      />

      <Navbar />

      <main className="pt-16">
        <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '关于我们' }]} />

        <section className="w-full py-12 md:py-16 px-4 md:px-6 bg-background-50">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="font-heading text-3xl md:text-5xl text-foreground-950 mb-4">
              {brandIntro.headline}
            </h1>
            <p className="text-lg text-primary-500 font-medium mb-6">{brandIntro.subheadline}</p>
            <p className="text-sm md:text-base text-foreground-600 leading-relaxed max-w-2xl mx-auto mb-8">
              {brandIntro.description}
            </p>
            <div className="grid grid-cols-3 gap-6 max-w-md mx-auto">
              <div className="text-center">
                <div className="font-heading text-2xl text-primary-500">{milestones.length}年</div>
                <div className="text-xs text-foreground-400 mt-1">发展历程</div>
              </div>
              <div className="text-center">
                <div className="font-heading text-2xl text-primary-500">{brandIntro.employees}</div>
                <div className="text-xs text-foreground-400 mt-1">团队成员</div>
              </div>
              <div className="text-center">
                <div className="font-heading text-2xl text-primary-500">{milestones[milestones.length - 1].year}</div>
                <div className="text-xs text-foreground-400 mt-1">持续创新</div>
              </div>
            </div>
          </div>
        </section>

        <section className="w-full py-12 md:py-16 px-4 md:px-6 bg-background-100">
          <div className="max-w-4xl mx-auto">
            <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 mb-2 text-center">
              我们的使命与愿景
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">
              <div className="bg-background-50 rounded-2xl p-6 md:p-8 border border-background-200/60">
                <div className="w-10 h-10 rounded-xl bg-primary-100 flex items-center justify-center mb-4">
                  <i className="ri-focus-2-line text-lg text-primary-500"></i>
                </div>
                <h3 className="font-heading text-lg text-foreground-950 mb-2">使命</h3>
                <p className="text-sm text-foreground-500 leading-relaxed">{brandIntro.mission}</p>
              </div>
              <div className="bg-background-50 rounded-2xl p-6 md:p-8 border border-background-200/60">
                <div className="w-10 h-10 rounded-xl bg-accent-100 flex items-center justify-center mb-4">
                  <i className="ri-eye-line text-lg text-accent-500"></i>
                </div>
                <h3 className="font-heading text-lg text-foreground-950 mb-2">愿景</h3>
                <p className="text-sm text-foreground-500 leading-relaxed">{brandIntro.vision}</p>
              </div>
            </div>
            <div className="mt-8 flex items-center justify-center gap-3 flex-wrap">
              {brandIntro.values.map((v) => (
                <span key={v} className="px-4 py-2 rounded-full bg-background-50 border border-background-200/60 text-sm text-foreground-600">
                  {v}
                </span>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full py-12 md:py-16 px-4 md:px-6 bg-background-50">
          <div className="max-w-4xl mx-auto">
            <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 mb-2 text-center">
              发展历程
            </h2>
            <p className="text-sm text-foreground-500 text-center mb-10">从0到2亿用户，每一步都踏实有力</p>
            <div className="relative">
              <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-0.5 bg-primary-200 md:-translate-x-px"></div>
              <div className="space-y-8">
                {milestones.map((m, idx) => (
                  <div key={idx} className={`relative flex items-start gap-6 md:gap-0 ${idx % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}>
                    <div className="md:w-1/2 flex md:justify-end md:pr-12">
                      <div className={`${idx % 2 === 0 ? 'md:text-right' : 'md:text-left md:pl-12'}`}>
                        <div className="bg-background-50 rounded-xl p-5 border border-background-200/60 ml-10 md:ml-0">
                          <div className="font-heading text-primary-500 text-lg mb-1">{m.year}</div>
                          <p className="text-sm text-foreground-600 leading-relaxed">{m.event}</p>
                        </div>
                      </div>
                    </div>
                    <div className="absolute left-4 md:left-1/2 w-4 h-4 rounded-full bg-primary-500 border-4 border-background-50 -translate-x-1/2 mt-6 z-10"></div>
                    <div className="md:w-1/2 hidden md:block"></div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="w-full py-12 md:py-16 px-4 md:px-6 bg-background-100">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 mb-2">{reputation.headline}</h2>
              <p className="text-sm text-foreground-500">{reputation.subheadline}</p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
              {reputation.stats.map((s, idx) => (
                <div key={idx} className="bg-background-50 rounded-2xl p-5 text-center border border-background-200/60">
                  <div className="font-heading text-2xl md:text-3xl text-primary-500 mb-1">{s.value}</div>
                  <div className="text-xs text-foreground-400">{s.label}</div>
                </div>
              ))}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-10">
              {reputation.userReviews.map((review, idx) => (
                <div key={idx} className="bg-background-50 rounded-2xl p-5 border border-background-200/60">
                  <div className="flex items-center gap-3 mb-4">
                    <img src={review.avatar} alt={review.name} className="w-10 h-10 rounded-full object-cover" />
                    <div>
                      <div className="text-sm font-medium text-foreground-800">{review.name}</div>
                      <div className="text-xs text-foreground-400">{review.role}</div>
                    </div>
                  </div>
                  <p className="text-sm text-foreground-500 leading-relaxed">{review.content}</p>
                </div>
              ))}
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {reputation.awards.map((award, idx) => (
                <div key={idx} className="bg-background-50 rounded-2xl p-5 text-center border border-background-200/60">
                  <div className="w-10 h-10 mx-auto mb-3 rounded-xl bg-accent-100 flex items-center justify-center">
                    <i className="ri-award-line text-lg text-accent-500"></i>
                  </div>
                  <div className="text-sm font-medium text-foreground-800 mb-1">{award.title}</div>
                  <div className="text-xs text-foreground-400">{award.issuer} · {award.year}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full py-12 md:py-16 px-4 md:px-6 bg-background-50">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 mb-2">{contact.headline}</h2>
              <p className="text-sm text-foreground-500">{contact.subheadline}</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl bg-primary-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-map-pin-line text-lg text-primary-500"></i>
                  </div>
                  <div>
                    <div className="text-sm font-medium text-foreground-800 mb-0.5">公司地址</div>
                    <div className="text-sm text-foreground-500">{contact.address}</div>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl bg-primary-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-phone-line text-lg text-primary-500"></i>
                  </div>
                  <div>
                    <div className="text-sm font-medium text-foreground-800 mb-0.5">客服电话</div>
                    <div className="text-sm text-foreground-500">{contact.phone}</div>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl bg-primary-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-mail-line text-lg text-primary-500"></i>
                  </div>
                  <div>
                    <div className="text-sm font-medium text-foreground-800 mb-0.5">电子邮箱</div>
                    <div className="text-sm text-foreground-500">{contact.email}</div>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl bg-primary-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-time-line text-lg text-primary-500"></i>
                  </div>
                  <div>
                    <div className="text-sm font-medium text-foreground-800 mb-0.5">工作时间</div>
                    <div className="text-sm text-foreground-500">{contact.workHours}</div>
                  </div>
                </div>
              </div>

              <div>
                <form id="contact-form" data-readdy-form onSubmit={handleSubmit} className="bg-background-100 rounded-2xl p-6 border border-background-200/60">
                  <h3 className="font-heading text-lg text-foreground-950 mb-5">发送消息</h3>

                  {formStatus === 'success' && (
                    <div className="mb-4 p-3 rounded-xl bg-green-50 border border-green-200 text-sm text-green-700">
                      消息已成功发送！我们会尽快与您联系。
                    </div>
                  )}
                  {formStatus === 'error' && (
                    <div className="mb-4 p-3 rounded-xl bg-red-50 border border-red-200 text-sm text-red-700">
                      {formError}
                    </div>
                  )}

                  <div className="space-y-4">
                    <div>
                      <label className="block text-xs font-medium text-foreground-600 mb-1.5">姓名</label>
                      <input
                        type="text"
                        name="name"
                        required
                        className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-800 focus:outline-none focus:border-primary-400 transition-colors"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-foreground-600 mb-1.5">邮箱</label>
                      <input
                        type="email"
                        name="email"
                        required
                        className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-800 focus:outline-none focus:border-primary-400 transition-colors"
                        placeholder="请输入您的邮箱"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-foreground-600 mb-1.5">主题</label>
                      <input
                        type="text"
                        name="subject"
                        required
                        className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-800 focus:outline-none focus:border-primary-400 transition-colors"
                        placeholder="请输入消息主题"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-foreground-600 mb-1.5">消息内容</label>
                      <textarea
                        name="message"
                        required
                        maxLength={500}
                        rows={4}
                        className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-800 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                        placeholder="请输入您的消息（最多500字）"
                      ></textarea>
                    </div>

                    <input
                      type="text"
                      name="website_alt"
                      className="antispam-field"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                    />

                    <button
                      type="submit"
                      disabled={submitting}
                      className="w-full py-3 rounded-full bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 transition-colors disabled:opacity-60 whitespace-nowrap"
                    >
                      {submitting ? '发送中...' : '发送消息'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}